import styled from "@emotion/styled";
import { inject, observer } from "mobx-react";
import React from "react";
import withTranslate, { WithTranslateProps } from "../common/WithTranslate";
import { Instance } from "mobx-state-tree";
import { AnchorStore, ApplicationStore } from "../../store";
import { Anchor, Group } from "../../store/models";
import { Page, TLCode } from "../../common/enums";
import {
    DataGrid,
    Column,
    Sorting,
    SearchPanel,
    FilterRow,
    FilterPanel,
    HeaderFilter,
    ColumnChooser,
    Pager,
    Paging,
    Button as BPButton
} from 'devextreme-react/data-grid';
import { ConfirmationDialog } from "../common/dialog/ConfirmationDialog";
import { TranslatedMessage } from "../common/translation";

const Container = styled.div`
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
`;

interface GroupAnchorsTabProps extends WithTranslateProps {
    readOnly: boolean;
    AnchorStore?: Instance<typeof AnchorStore>;
    ApplicationStore?: Instance<typeof ApplicationStore>;
    group?: Instance<typeof Group>;
}

interface GroupAnchorsTabState {
    isLoading: boolean;
    anchorToDelete: Instance<typeof Anchor> | undefined;
    isConfirmationDialogOpen: boolean;
}

@inject('ApplicationStore')
@inject('AnchorStore')
@observer
class GroupAnchorsTab extends React.Component<GroupAnchorsTabProps, GroupAnchorsTabState> {
    state: GroupAnchorsTabState = {
        isLoading: false,
        anchorToDelete: undefined,
        isConfirmationDialogOpen: false
    };

    _isMounted = false;

    async componentDidMount() {
        this._isMounted = true;
        this._isMounted && this.setState({ isLoading: true });
        await this.props.AnchorStore!.checkListLoaded();
        this._isMounted && this.setState({ isLoading: false });
    }

    componentWillUnmount() {
        this._isMounted = false;
    }

    componentDidUpdate(prevProps: GroupAnchorsTabProps) {
        if (prevProps.group !== this.props.group) {
            this.buildGridData();
        }
    }

    private buildGridData = () => {
        return this.props
            .AnchorStore!.dataList.filter((elem) => {
                return this.props.group !== undefined && elem.groupId === this.props.group!.groupId;
            })
            .map((p) => ({
                anchorId: p.anchorId,
                name: p.name,
                color: p.color,
            }));
    };

    confirmAnchorDeletion = async () => {
        if (this.state.anchorToDelete !== undefined) {
            try {
                await this.props.AnchorStore!.deleteElement(this.state.anchorToDelete);
                this.setState({ isConfirmationDialogOpen: false, anchorToDelete: undefined });
            } catch (error) {
                console.error('Failed to delete anchor:', error);
            }
        }
    };

    goToAnchorForm = (id: string) => {
        var groupId = this.props.group ? this.props.group.groupId : 0;
        this.props.ApplicationStore!.navigate(`${Page.Anchor}/${id}?from=groupAnchorsTab&group=${groupId}`);
    }

    render() {
        const { translate } = this.props;
        const { isConfirmationDialogOpen, anchorToDelete } = this.state;
        return (
            <>
                <ConfirmationDialog
                    isOpen={isConfirmationDialogOpen}
                    onContinue={this.confirmAnchorDeletion}
                    title={translate(TLCode.ANCHOR_Delete_Confirmation_Dialog_Title)}
                    message={translate(TLCode.ANCHOR_Delete_Confirmation_Dialog_Message, {
                        anchorName: anchorToDelete !== undefined ? anchorToDelete.name : '',
                    })}
                    confirmText={translate(TLCode.GENERAL_Confirm)}
                    cancelText={translate(TLCode.GENERAL_Cancel)}
                    onCancel={() => this.setState({ isConfirmationDialogOpen: false, anchorToDelete: undefined })}
                />
                <Container>
                    <br />
                    {this.buildGridData().length > 0 ? (
                        <>
                            <br />
                            <br />
                            <DataGrid
                                id="pharmaciesList"
                                dataSource={this.buildGridData()}
                                showBorders
                                allowColumnResizing
                                rowAlternationEnabled={true}
                                onRowClick={(ev) => {
                                    var anchorId = ev.data.anchorId;
                                    this.goToAnchorForm(anchorId);
                                }}
                            >
                                <Sorting mode="multiple" />
                                <FilterRow visible={true} applyFilter={'auto'} />
                                <SearchPanel visible={true} highlightCaseSensitive={false} width={400} />
                                <HeaderFilter visible={true} />
                                <FilterPanel visible={true} />
                                <ColumnChooser enabled={true} />
                                <Column
                                    dataType="string"
                                    dataField="name"
                                    caption={translate(TLCode.ANCHOR_Name)} defaultSortIndex={1} sortOrder="asc"
                                    defaultSortOrder="asc"
                                    alignment="center"
                                    sortIndex={1}
                                />
                                <Column
                                    dataType="string"
                                    dataField="color"
                                    alignment="center"
                                    caption={translate(TLCode.ANCHOR_Color)}
                                />
                                <Column caption="Actions" type="buttons" width={60}>
                                    <BPButton
                                        hint={translate(TLCode.GENERAL_Edit)}
                                        icon={'edit'}
                                        onClick={(e) => {
                                            this.goToAnchorForm(e.row!.data.anchorId);
                                        }}
                                    />
                                    <BPButton
                                        hint={translate(TLCode.GENERAL_Delete)}
                                        icon={'clear'}
                                        onClick={(e) => {
                                            this.setState({ anchorToDelete: e.row!.data, isConfirmationDialogOpen: true });
                                        }}
                                    />
                                </Column>
                                <Pager
                                    visible={true}
                                    allowedPageSizes={[5, 10, 20, 50, 100]}
                                    showPageSizeSelector={true}
                                    showInfo={true}
                                    showNavigationButtons={true}
                                />
                                <Paging defaultPageSize={20} />
                            </DataGrid>
                        </>
                    ) : (
                        <div>
                            <TranslatedMessage id={TLCode.ANCHORGROUP_No_anchors} />
                        </div>
                    )
                    }
                </Container>
            </>
        );
    }
}

export default withTranslate(GroupAnchorsTab);